<script setup>
import { useTheme } from 'vuetify'
import ScrollToTop from '@core/components/ScrollToTop.vue'
import { useThemeConfig } from '@core/composable/useThemeConfig'
import { hexToRgb } from '@layouts/utils'
import { useMainStore } from "@/stores/MainStore"
import { useAuthStore } from '@/stores/AuthStore'
import { useUserStore } from "@/stores/UserStore"
import Echo from 'laravel-echo'
import Pusher from 'pusher-js'
import { isUserLoggedIn } from '@/router/utils'
import { useCookies } from "vue3-cookies"
import getUserType from "@/helpers/getUserType"
import { useRoute, useRouter } from 'vue-router'
import eventTracker from '@/helpers/eventTracker'
import AppAbility from '@/plugins/casl/useAppAbility'
import * as Sentry from '@sentry/vue'

const ability = AppAbility.useAppAbility()

const {
  isAppRtl,
  handleSkinChanges,
} = useThemeConfig()

const mainStore = useMainStore()
const authStore = useAuthStore()
const userStore = useUserStore()

const statusRetrieved = localStorage.getItem('accountStatus')
const accountStatus = statusRetrieved && statusRetrieved !== 'undefined' ? JSON.parse(statusRetrieved) : null

// Listen for unhandled promise rejections
window.addEventListener('unhandledrejection', e => {
  const reasonObj = e.reason
  let errorToCapture = reasonObj

  // If reason is not an Error, normalize it so Sentry gets something useful
  if (!(reasonObj instanceof Error)) {
    errorToCapture = new Error(typeof reasonObj === 'string' ? reasonObj : JSON.stringify(reasonObj, null, 2))
  }

  if (import.meta.env.VITE_APP_ENVIRONMENT !== 'prod') {
    console.debug('Dev note - global unhandledrejection handler called', errorToCapture)
  }

  Sentry.captureException(errorToCapture)

  eventTracker.trackEvent({
    event: 'unhandledrejection_error_thrown',
    data: { error: reasonObj },
  })
})

if (isUserLoggedIn()) {
  const { cookies } = useCookies()
  const JWtoken = cookies.get("jwt")
  const isUserTypeRetailer = getUserType() === 'retailer'
  const userData = JSON.parse(localStorage.getItem('userData') || '{}')

  userStore.getUserProfile()

  window.Pusher = Pusher

  window.Pusher.logToConsole = ['local', 'dev'].includes(import.meta.env.VITE_APP_ENVIRONMENT)

  window.Echo = new Echo({
    broadcaster: 'pusher',
    key: import.meta.env.VITE_APP_KEY,
    cluster: import.meta.env.VITE_APP_CLUSTER,
    forceTLS: true,
    authEndpoint: `${ import.meta.env.VITE_APP_BE_URL }broadcasting/auth/`,
    auth: {
      headers: {
        'Authorization': `Bearer ${ JWtoken }`,
      },
    },
  })

  window.Echo.private(`users`)
    .listen('.Domains\\Portal\\App\\Events\\IndividualUserNotification', async e => {
      userStore.reloadList = false
      await  mainStore.addMessage(e.data.type ? e.data.type : 'info', e.data.message, e.data.title)
      userStore.reloadList = true
    })

  // Individual user channel
  window.Echo.private(`users.${ userData.user_id }`)
    .listen('.Domains\\Portal\\App\\Events\\IndividualUserNotification', async e => {
      userStore.reloadList = false
      await mainStore.addMessage(e.data.type ? e.data.type : 'info', e.data.message, e.data.title)
      userStore.reloadList = true
    })

  if (isUserTypeRetailer) {
    // all retailer channel
    // if user type is retailer
    window.Echo.private('retailers')
      .listen('.Domains\\Portal\\App\\Events\\AllRetailerNotification', async e => {
        userStore.reloadList = false
        await mainStore.addMessage(e.data.type ? e.data.type : 'info', e.data.message, e.data.title)
        userStore.reloadList = true
      })

    // all users belonging to a specific retailer
    // if (user.retailer_id)
    window.Echo.private(`retailers.${ userData.retailer_id }`)
      .listen('.Domains\\Portal\\App\\Events\\RetailerNotification', async e => {
        userStore.reloadList = false
        await mainStore.addMessage(e.data.type ? e.data.type : 'info', e.data.message, e.data.title)
        userStore.reloadList = true
      })
  }
  else {
    // all retailer channel
    // if user type is retailer
    window.Echo.private('publishers')
      .listen('.Domains\\Portal\\App\\Events\\AllPublisherNotification', async e => {
        userStore.reloadList = false
        await mainStore.addMessage(e.data.type ? e.data.type : 'info', e.data.message, e.data.title)
        userStore.reloadList = true
      })

    // all users belonging to a specific retailer
    // if (user.retailer_id)
    window.Echo.private(`publishers.${ userData.publisher_id }`)
      .listen('.Domains\\Portal\\App\\Events\\PublisherNotification', async e => {
        userStore.reloadList = false
        await mainStore.addMessage(e.data.type ? e.data.type : 'info', e.data.message, e.data.title)
        userStore.reloadList = true
      })
  }
}

const { global } = useTheme()

handleSkinChanges()

authStore.updateAbility(ability)
</script>

<template>
  <VLocaleProvider :rtl="isAppRtl">
    <!-- ℹ️ This is required to set the background color of active nav link based on currently active global theme's primary -->
    <VApp :style="`--v-global-theme-primary: ${hexToRgb(global.current.value.colors.primary)}`">
      <RouterView />
      <ScrollToTop />
      <OnBoardingSteps
        v-if="accountStatus !== 'approved' && !userStore.isOnboarded && userStore.completeOnboardingSteps"
        :completed-steps="userStore.completeOnboardingSteps"
      />
      <Teleport to="body">
        <div class="position-fixed notification-container d-flex justify-start flex-column align-center">
          <template
            v-for="message in mainStore.messages"
            :key="message"
          >
            <VAlert
              v-if="message.visible"
              :title="message.title ? message.title : ''"
              class="notification-alert flex-0-0 ma-1"
              :color="message.type"
            >
              {{ message.message }}
            </VAlert>
          </template>
        </div>
      </Teleport>
    </VApp>
  </VLocaleProvider>
</template>

<script type="text/javascript">
window.csrf_token = "{{ csrf_token() }}"
</script>

<style lang="scss">
  .notification-container {
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    z-index: 2500;
    pointer-events: none;
  }
</style>
